

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { blogData } from "../Blog/BlogData";
import { Calendar, ChevronRight, ChevronLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";

const Blogs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  const sortedBlogs = [...blogData].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = sortedBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(sortedBlogs.length / blogsPerPage);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  return (
    <>
    <Helmet>
        <title>Legal Library - Latest Legal Insights | SKS Law</title>
        <meta
          name="description"
          content="Explore our Legal Library for the latest legal insights, expert analysis, and updates on various legal topics. Stay informed with SKS Law's trusted legal resources."
        />

        <noscript>
          Please enable JavaScript to view this website properly.
        </noscript>
      </Helmet>
    <section className="relative pt-40 pb-24 px-6 lg:px-20 overflow-hidden">

      {/* 🔥 PREMIUM BROWN BACKGROUND */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FAF9F6] via-[#F3EDE7] to-[#E8DED2]" />

      <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_1px_1px,#A47148_1px,transparent_0)] bg-[size:22px_22px]" />

      <div className="relative max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="text-center mb-20">
          <p className="text-[#A47148] uppercase tracking-[0.4em] text-sm font-semibold">
            Legal Library
          </p>

          <h2 className="text-4xl md:text-5xl font-semibold text-black mt-4">
            Latest Legal Insights
          </h2>

          <div className="w-20 h-[2px] bg-[#A47148] mx-auto mt-6"></div>
        </div>

        {/* BLOG GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">

          {currentBlogs.map((blog) => (
            <Link
              to={`/blogs/${blog.id}`}
              key={blog.id}
              className="group rounded-2xl overflow-hidden bg-white/80 backdrop-blur-md border border-[#E5DED6] hover:shadow-xl hover:-translate-y-2 transition-all duration-500"
            >

              {/* IMAGE */}
              <div className="relative h-60 overflow-hidden">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                />

                {/* CATEGORY */}
                <div className="absolute top-4 left-4 bg-black/80 text-white text-[10px] px-3 py-1 uppercase tracking-wider rounded">
                  {blog.category}
                </div>
              </div>

              {/* CONTENT */}
              <div className="p-6 flex flex-col">

                {/* DATE */}
                <div className="flex items-center gap-2 text-gray-500 text-[11px] mb-3 uppercase tracking-wide">
                  <Calendar size={14} className="text-[#A47148]" />
                  {new Date(blog.date).toLocaleDateString()}
                </div>

                {/* TITLE */}
                <h3 className="text-black text-lg font-semibold mb-3 line-clamp-2 group-hover:text-[#A47148] transition">
                  {blog.title}
                </h3>

                {/* DESC */}
                <p className="text-gray-600 text-sm mb-6 line-clamp-3">
                  {blog.excerpt}
                </p>

                {/* READ */}
                <div className="flex items-center justify-between mt-auto">

                  <span className="text-sm text-[#A47148] opacity-0 group-hover:opacity-100 transition">
                    Read More →
                  </span>

                  <div className="h-[2px] w-0 group-hover:w-10 bg-[#A47148] transition-all duration-500" />

                </div>

              </div>
            </Link>
          ))}

        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="mt-24 flex justify-center items-center gap-3">

            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-black hover:text-white transition disabled:opacity-30"
            >
              <ChevronLeft size={18} />
            </button>

            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index + 1)}
                className={`px-4 py-2 rounded border transition ${
                  currentPage === index + 1
                    ? "bg-black text-white border-black"
                    : "bg-white text-black border-gray-300 hover:border-[#A47148]"
                }`}
              >
                {index + 1}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(prev + 1, totalPages)
                )
              }
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-black hover:text-white transition disabled:opacity-30"
            >
              <ChevronRight size={18} />
            </button>

          </div>
        )}

      </div>
    </section>
    </>
  );
};

export default Blogs;